import { create } from 'zustand';
import type { BossDifficulty, BossInputEvent } from '@shared/boss';
import {
  bossApi,
  type BossCompetitionOverview,
  type BossListItem,
  type BossRotation,
  type BossRunSettlement,
  type BossRunTicket,
} from '@/boss/bossApi';
import { clanApi } from '@/clan/clanApi';

interface BossState {
  enabled: boolean;
  bosses: BossListItem[];
  rotation: BossRotation | null;
  competition: BossCompetitionOverview | null;
  loading: boolean;
  error: string | null;
  activeRun: BossRunTicket | null;
  settlement: BossRunSettlement | null;
  load: () => Promise<void>;
  start: (bossId: string, difficulty: BossDifficulty) => Promise<void>;
  startClan: () => Promise<void>;
  finish: (
    elapsedMs: number,
    events: readonly BossInputEvent[],
  ) => Promise<BossRunSettlement>;
  clearRun: () => void;
}

export const useBossStore = create<BossState>((set, get) => ({
  enabled: false,
  bosses: [],
  rotation: null,
  competition: null,
  loading: false,
  error: null,
  activeRun: null,
  settlement: null,
  load: async () => {
    set({ loading: true, error: null });
    try {
      const response = await bossApi.list();
      set({
        enabled: response.enabled,
        bosses: response.bosses,
        rotation: response.rotation ?? null,
        competition: response.competition ?? null,
        loading: false,
      });
    } catch (error) {
      set({ loading: false, error: String(error) });
    }
  },
  start: async (bossId, difficulty) => {
    set({ loading: true, error: null, settlement: null });
    try {
      const activeRun = await bossApi.begin(bossId, difficulty);
      set({ activeRun, loading: false });
    } catch (error) {
      set({ loading: false, error: String(error) });
      throw error;
    }
  },
  startClan: async () => {
    set({ loading: true, error: null, settlement: null });
    try {
      const activeRun = await clanApi.beginClanBoss();
      set({ activeRun, loading: false });
    } catch (error) {
      set({ loading: false, error: String(error) });
      throw error;
    }
  },
  finish: async (elapsedMs, events) => {
    const run = get().activeRun;
    if (!run) throw new Error('No active boss run');
    const settlement = run.mode === 'clan'
      ? await clanApi.finishClanBoss(run.runId, elapsedMs, events)
      : await bossApi.finish(run.runId, elapsedMs, events);
    set({ settlement });
    return settlement;
  },
  clearRun: () => set({ activeRun: null, settlement: null, error: null }),
}));
